/**
 * ч.1112 (рішення власника): на головній — кілька рядків із чек-апу після
 * міграції, щоб бухгалтер не забув доробити перенесення; картка зникає сама,
 * коли зауважень не лишилось або перенесення не було. ч.1411 (рішення власника):
 * власник може сховати її в «налаштувати віджети» — сам чек-ап лишається у розділі.
 * Організації — ті, де є право на міграцію або право перегляду чек-апу;
 * фільтр організації у шапці звужує. Дані — GET /migration/checkup/summary
 * (сервер кешує 10 хв).
 */
import { useMemo, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { CHECKUP_RIGHTS, migrationCheckup } from '@/api/migrationRuns';
import { useAllPermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useOrgFilter } from '@/store/defaults';
import { SEVERITY_LABEL, severityTokens, summaryLine } from '@/pages/checkupModel';
import uiPrefs from '@/store/uiPrefs';

/** ч.1411 (рішення власника): картку можна сховати у «налаштувати віджети» — вибір у профілі. */
export const CHECKUP_HIDDEN_KEY = 'dash-widgets:checkup-hidden';

export function useCheckupHidden(): boolean {
  return useSyncExternalStore(
    (cb) => uiPrefs.subscribe(CHECKUP_HIDDEN_KEY, cb),
    () => uiPrefs.get<boolean>(CHECKUP_HIDDEN_KEY) === true,
    () => false,
  );
}

/** Організації, де є що доробити після перенесення. */
export function useCheckupBlocks() {
  const all = useAllPermissionsByOrg();
  const [orgFilter] = useOrgFilter();
  const orgs = useMemo(
    () => all.allOrgs
      .filter((o) => !orgFilter || o.id === orgFilter)
      .filter((o) => (all.permissionsByOrg[o.id] ?? [])
        .some((p) => (CHECKUP_RIGHTS as readonly string[]).includes(p))),
    [all.allOrgs, all.permissionsByOrg, orgFilter]);

  const results = useQueries({
    queries: orgs.map((o) => ({
      queryKey: ['migration-checkup-summary', o.id],
      queryFn: () => migrationCheckup.summary(o.id),
      staleTime: 5 * 60_000,
      retry: false,
    })),
  });
  return orgs
    .map((o, i) => ({ org: o, data: results[i]?.data }))
    .filter((b) => b.data?.has_migration && (b.data.summary?.total ?? 0) > 0);
}

export default function MigrationCheckupBanner() {
  const blocks = useCheckupBlocks();
  if (blocks.length === 0) return null;

  return (
    <div className="card" data-testid="migration-checkup-banner"
      style={{ marginTop: '0.75rem', borderLeft: '4px solid var(--badge-amber-bd)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>Чек-ап після міграції: є що доробити</h3>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          зауваження зникають самі, щойно факт виправлено
        </span>
      </div>
      {blocks.map(({ org, data }) => (
        <div key={org.id} style={{ marginTop: '0.6rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
            {blocks.length > 1 && <strong>{org.name}</strong>}
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              {summaryLine(data!.summary!)}
            </span>
            <Link to={`/migration/checkup?org=${org.id}`} style={{ marginLeft: 'auto' }}>
              відкрити чек-ап →
            </Link>
          </div>
          <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.1rem' }}>
            {data!.top.map((f) => {
              const t = severityTokens(f.severity);
              return (
                <li key={f.code} style={{ margin: '0.15rem 0' }}>
                  <span style={{
                    color: t.fg, background: t.bg, border: `1px solid ${t.bd}`, borderRadius: 4,
                    padding: '0 0.35rem', fontSize: '0.72rem', fontWeight: 600, marginRight: '0.5rem',
                    whiteSpace: 'nowrap',
                  }}>{SEVERITY_LABEL[f.severity]}</span>
                  {f.link ? <Link to={f.link}>{f.title}</Link> : f.title}
                  <span style={{ color: 'var(--muted)' }}> — {f.count.toLocaleString('uk-UA')}</span>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
